/** Display name for a task row or API task document. */
export function resolveTaskDisplayName(taskOrRow) {
  if (!taskOrRow) return "";
  return String(taskOrRow.name || taskOrRow.title || "").trim();
}

/** Display name for a task template row or API document. */
export function resolveTemplateDisplayName(templateOrRow) {
  return resolveTaskDisplayName(templateOrRow);
}

/** In-app toast: prefix with entity name when available. */
export function formatTaskToast(taskName, message) {
  const name = String(taskName || "").trim();
  const text = String(message || "").trim();
  if (!name) return text;
  if (!text) return name;
  return `${name}: ${text}`;
}

/** In-app toast for task templates. */
export function formatTemplateToast(templateName, message) {
  return formatTaskToast(templateName, message);
}

/** Small in-memory hint when socket log omits taskName (e.g. older backend). */
const taskNameHintById = new Map();
const TASK_NAME_HINT_MAX = 500;

export function rememberTaskDisplayName(taskId, name) {
  const id = String(taskId || "").trim();
  const label = String(name || "").trim();
  if (!id || !label) return;
  taskNameHintById.set(id, label);
  if (taskNameHintById.size > TASK_NAME_HINT_MAX) {
    const oldest = taskNameHintById.keys().next().value;
    taskNameHintById.delete(oldest);
  }
}

function findTaskNameInRows(rows, taskId) {
  if (!Array.isArray(rows)) return "";
  const row = rows.find((r) => String(r.id || r._id) === taskId);
  return row ? resolveTaskDisplayName(row) : "";
}

/** Resolve task name from loaded Redux tasks state (no network; scans in-memory list only). */
export function resolveTaskDisplayNameFromTasksState(tasksState, taskId) {
  const id = String(taskId || "").trim();
  if (!id || !tasksState) return "";

  const selected = tasksState.selectedTask;
  if (selected) {
    const sid = String(selected._id || selected.id || "");
    if (sid === id) return resolveTaskDisplayName(selected);
  }

  let name = findTaskNameInRows(tasksState.rows, id);
  if (name) return name;

  const grouped = tasksState.grouped;
  if (!grouped) return "";

  name = findTaskNameInRows(grouped.leaderTasks, id);
  if (name) return name;

  name = findTaskNameInRows(grouped.unassignedTasks, id);
  if (name) return name;

  const members = grouped.members;
  if (Array.isArray(members)) {
    for (let i = 0; i < members.length; i += 1) {
      name = findTaskNameInRows(members[i]?.tasks, id);
      if (name) return name;
    }
  }
  return "";
}

/**
 * Task name for browser notifications: socket payload → Redux list → recent hint cache.
 */
export function resolveTaskDisplayNameForNotification(tasksState, taskId, payloadTaskName) {
  const fromPayload = String(payloadTaskName || "").trim();
  if (fromPayload) return fromPayload;

  const id = String(taskId || "").trim();
  if (!id) return "";

  const fromStore = resolveTaskDisplayNameFromTasksState(tasksState, id);
  if (fromStore) return fromStore;

  return String(taskNameHintById.get(id) || "").trim();
}

/** Browser notification title for task activity log events. */
export function buildTaskLogNotificationTitle(taskName, fallbackTitle = "Task log") {
  const name = String(taskName || "").trim();
  return name ? `Task · ${name}` : fallbackTitle;
}

/** Browser notification body for task activity log events. */
export function buildTaskLogNotificationBody(actorName, summaryLine) {
  const line = String(summaryLine || "").trim() || "Task activity updated";
  const actor = String(actorName || "").trim();
  let body = line;
  if (actor && !line.startsWith(actor)) {
    body = `${actor}: ${line}`;
  }
  if (body.length > 120) {
    return `${body.slice(0, 120)}…`;
  }
  return body;
}
